import {
  CellClassParams,
  CellClickedEvent,
  CellKeyDownEvent,
  CellStyle,
  ColDef,
  ColGroupDef,
  GridApi,
  EditableCallbackParams,
  ICellRendererParams,
  SuppressKeyboardEventParams,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
  GridOptions,
} from 'ag-grid-community'
import {
  ColumnType,
  columnTypes,
  dateValueFormatter,
  defaultOnCellClicked,
  frameworkComponents,
} from '../../../containers/commons/AgGrid'
import {
  CheckBoxCellRenderer,
  CustomEnumCellRenderer,
  DefaultCellRenderer,
} from '../../../containers/BulkSheetView/components/cellRenderer'
import _ from 'lodash'
import objects from '../../../../utils/objects'
import store from '../../../../store'
import { requireSave } from '../../../../store/requiredSaveData'
import { ServerSideUuidFilter } from '../../../containers/BulkSheetView/components/filter'
import { intl } from '../../../../i18n'
import {
  CheckBoxCellEditor,
  CustomEnumCellEditor,
} from '../../../containers/BulkSheetView/components/cellEditor'
import { DISPLAY_DATE_SHORT_FORMAT_WITH_DAY } from '../../../../utils/date'
import './styles.scss'
import { ProjectDetail } from '../../../../lib/functions/project'
import { openTaskActualWorkListProps } from '../hooks/taskActualWorkTaskDialogState'
import { Color } from '../../../../styles/commonStyles'
import { featureFlagService } from '../../../../utils/feature-flag'
import {
  ActualWorkingHoursRow,
  formatTime,
  formatWorkTime,
  isWorkingDay,
  isRequiredStartTime,
  RequestStatus,
  WorkingHoursRowIncludingCalculated,
  WorkingHoursType,
} from '../../ActualWorkingHours/ActualWorkingHours'
import DateVO from '../../../../vo/DateVO'

import {
  calculateRatio,
  DISCREPANCY_CELL_STYLE,
} from '../../ActualWorkingHours/gridOptions'
import TotalActualHourCellRenderer from '../../ActualWorkingHours/components/CellRendere/TotalActualHourCellRenderer'
import RequestStatusCellRenderer from '../../ActualWorkingHours/components/CellRendere/RequestStatusCellRenderer'
import { isValidApprove, isValidSentBack } from '../hooks/taskActualWorkData'
import { customHeaderTemplate } from '../../../containers/commons/AgGrid/components/header/CustomHeader'

export const actualWorkingHoursGridOptions = ({
  editable,
  openTaskActualWorkList,
  callbackCellEdit,
}: {
  editable: boolean
  openTaskActualWorkList?: (props: openTaskActualWorkListProps) => void
  callbackCellEdit: () => void
}): GridOptions => {
  return {
    groupHeaderHeight: 32,
    headerHeight: 45,
    rowHeight: 32,
    treeData: false,
    context: {},
    suppressLastEmptyLineOnPaste: true,
    tooltipShowDelay: 500,
    // Row
    rowDragManaged: false,
    rowDragMultiRow: false,
    suppressMoveWhenRowDragging: true,
    enterNavigatesVerticallyAfterEdit: false,
    onCellValueChanged: event => {
      callbackCellEdit()
    },
    onCellKeyDown: (params: CellKeyDownEvent) => {
      // @ts-ignore
      if (!['Delete', 'Backspace'].includes(params.event.key)) return
      const cellRanges = params.api.getCellRanges() || []
      cellRanges.forEach(range => {
        const start = Math.min(range.startRow!.rowIndex, range.endRow!.rowIndex)
        const end = Math.max(range.startRow!.rowIndex, range.endRow!.rowIndex)
        range.columns.forEach(column => {
          const colDef = column.getColDef()
          if (
            typeof colDef.editable === 'function'
              ? colDef.editable(params)
              : colDef.editable
          ) {
            for (let i = start; i <= end; i++) {
              const rowNode = params.api.getDisplayedRowAtIndex(i)
              rowNode?.setDataValue(column, undefined)
            }
          }
        })
      })
    },
    // Column
    columnTypes: columnTypes(),
    components: frameworkComponents,
    defaultColDef: {
      cellDataType: false,
      width: 85,
      editable: (
        params: EditableCallbackParams<WorkingHoursRowIncludingCalculated>
      ) => {
        const data = params.data
        if (!data) return false
        if (data.type === 'SUM' || data.type === 'RATIO') {
          return false
        }
        return editable
      },
      enableValue: false,
      sortable: false,
      resizable: true,
      suppressHeaderMenuButton: true,
      suppressSizeToFit: true,
      singleClickEdit: false,
      cellEditor: 'textEditor',
      cellStyle: { justifyContent: 'center' },
      cellClassRules: {
        'sevend-ag-grid-date-header-holiday': params => {
          const types = [
            WorkingHoursType.PaidLeave,
            WorkingHoursType.PaidLeaveHalf,
            WorkingHoursType.SpecialLeave,
            WorkingHoursType.Holiday,
          ]
          const data = params.data
          if (data.type === 'SUM' || data.type === 'RATIO') {
            return false
          } else if (
            data.type === 'WORK_HOUR' &&
            (!!data.schedule?.legal ||
              data.schedule.workHour === 0 ||
              types.includes(data.workingHoursType))
          ) {
            return true
          }
          return false
        },
      },
      cellRenderer: DefaultCellRenderer,
      onCellClicked: defaultOnCellClicked,
      suppressPaste: params => !_.isEmpty(params.context?.copied),
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        return (
          !params.editing && ['Delete', 'Backspace'].includes(params.event.key)
        )
      },
      valueSetter: (params: ValueSetterParams) => {
        const field = params.colDef.field || params.colDef.colId
        if (
          !field ||
          (!params.oldValue && !params.newValue) ||
          params.oldValue === params.newValue
        ) {
          return false
        }
        objects.setValue(params.data, field, params.newValue)
        params.data.edited = true
        store.dispatch(requireSave())
        return true
      },
    },
    columnDefs: [
      {
        field: 'uuid',
        hide: true,
        suppressColumnsToolPanel: true,
        filter: ServerSideUuidFilter,
      },
      {
        colId: 'blank',
        hide: true,
        width: 35,
        suppressColumnsToolPanel: true,
      },
      {
        field: 'date',
        headerName: intl.formatMessage({ id: 'actualWorkingHours.date' }),
        hide: false,
        pinned: true,
        lockPosition: 'left',
        width: 90,
        editable: false,
        type: [ColumnType.date],
        valueFormatter: (params: ValueFormatterParams) =>
          dateValueFormatter(
            params.value,
            DISPLAY_DATE_SHORT_FORMAT_WITH_DAY
          ) || '',
      },
      {
        field: 'workingHoursType',
        headerName: intl.formatMessage({
          id: 'actualWorkingHours.workingHoursType',
        }),
        hide: false,
        pinned: true,
        lockPosition: 'left',
        width: 140,
        editable: false,
        type: [ColumnType.customEnum],
        cellEditor: CustomEnumCellEditor,
        cellEditorParams: {
          customEnumCode: 'workingHoursType',
        },
        cellRenderer: CustomEnumCellRenderer,
        cellRendererParams: {
          customEnumCode: 'workingHoursType',
        },
        cellClass: 'workingHoursType',
      },
      {
        field: 'actual.startTime',
        headerName: intl.formatMessage({ id: 'actualWorkingHours.startTime' }),
        hide: false,
        pinned: true,
        editable: false,
        lockPosition: 'left',
        width: 100,
        cellClass: 'actualTime',
        cellClassRules: {
          'actual-hours-required-input': params => {
            const data = params.data
            if (data.type === 'SUM' || !data.actual) return false
            return isRequiredOtherSide(
              data.actual.endTime,
              data.actual.startTime
            )
          },
          'actual-hours-required-start-time': params => {
            return isRequiredStartTime(params.data)
          },
        },
      },
      {
        field: 'actual.endTime',
        headerName: intl.formatMessage({ id: 'actualWorkingHours.endTime' }),
        hide: false,
        pinned: true,
        editable: false,
        lockPosition: 'left',
        width: 100,
        cellClass: 'actualTime',
        cellClassRules: {
          'actual-hours-required-input': params => {
            const data = params.data
            if (data.type === 'SUM' || !data.actual) return false
            return isRequiredOtherSide(
              data.actual.startTime,
              data.actual.endTime
            )
          },
        },
      },
      {
        field: 'actual.breakTime',
        headerName: intl.formatMessage({ id: 'actualWorkingHours.breakTime' }),
        hide: false,
        pinned: true,
        editable: false,
        lockPosition: 'left',
        width: 100,
        valueGetter: (
          params: ValueGetterParams<WorkingHoursRowIncludingCalculated>
        ) => {
          const data = params.data
          if (!data) return undefined
          if (data.type === 'SUM') {
            return data.breakTime ? formatTime(data.breakTime) : ''
          } else if (data.type === 'WORK_HOUR') {
            return data.actual?.breakTime ?? ''
          }
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return params.value
        },
        cellClass: (
          params: CellClassParams<WorkingHoursRowIncludingCalculated>
        ) => {
          const data = params.data
          if (!data) return ''
          if (data.type === 'SUM') {
            return 'timeFormula'
          } else if (data.type === 'WORK_HOUR') {
            return 'breakTime'
          }
        },
      },
      {
        field: 'actual.workedTime',
        headerName: intl.formatMessage({ id: 'actualWorkingHours.workedTime' }),
        hide: false,
        pinned: true,
        lockPosition: 'left',
        width: 90,
        editable: false,
        valueGetter: (
          params: ValueGetterParams<WorkingHoursRowIncludingCalculated>
        ) => {
          const data = params.data
          if (!data) return undefined
          if (data.type === 'SUM') {
            return formatTime(data.workHour)
          } else if (data.type === 'WORK_HOUR') {
            return formatWorkTime(data)
          }
        },
        cellClass: 'timeFormula',
      },
      {
        field: 'remarks',
        headerName: intl.formatMessage({ id: 'actualWorkingHours.remarks' }),
        hide: false,
        pinned: true,
        editable: false,
        lockPosition: 'left',
        width: 200,
        cellStyle: { justifyContent: 'start' },
      },
      {
        field: 'totalActualHour',
        headerName: intl.formatMessage({
          id: 'actualWorkingHours.actualHour',
        }),
        hide: false,
        pinned: true,
        lockPosition: 'left',
        width: 85,
        editable: false,
        valueGetter: (
          params: ValueGetterParams<WorkingHoursRowIncludingCalculated>
        ) => {
          const data = params.data
          if (!data) return undefined
          if (data.type === 'RATIO') {
            return []
          } else {
            const value = data.totalActualHour || 0
            if (value === 0) return ''
            return formatTime(value)
          }
        },
        cellClass: 'actualTime',
        cellRenderer: TotalActualHourCellRenderer,
        cellStyle: featureFlagService.isEnabled('task-actual-work-application')
          ? params => {
              const data = params.data as ActualWorkingHoursRow
              if (!data || data.type !== 'WORK_HOUR') return undefined

              const formatTotalProjectHours = formatTime(
                data.projectHours?.reduce(
                  (sum, project) => sum + (project.actualHour || 0),
                  0
                ) || 0
              )
              const actualHour = formatWorkTime(data)
              const today = new DateVO().formatForApi()
              let discrepancyStyle = {}
              if (data.date && today && data.date <= today) {
                if (isWorkingDay(data.workingHoursType as WorkingHoursType)) {
                  if (formatTotalProjectHours === '0:00' && actualHour === '') {
                    return
                  }
                  if (formatTotalProjectHours !== actualHour) {
                    discrepancyStyle = DISCREPANCY_CELL_STYLE
                  }
                }
              }
              return {
                justifyContent: 'center',
                ...discrepancyStyle,
              }
            }
          : { justifyContent: 'center' },
      },
    ],
    excelStyles: [
      {
        id: 'dateType',
        dataType: 'DateTime',
        numberFormat: {
          format: intl.formatMessage({
            id: 'actualWorkingHours.excel.date.format',
          }),
        },
      },
      {
        id: 'actualTime',
        alignment: {
          horizontal: 'Center',
        },
      },
      {
        id: 'breakTime',
        numberFormat: {
          format: intl.formatMessage({
            id: 'h:mm',
          }),
        },
        alignment: {
          horizontal: 'Center',
        },
      },
      {
        id: 'workingHoursType',
        alignment: {
          horizontal: 'Center',
        },
      },
      {
        id: 'timeFormula',
        dataType: 'Formula',
        numberFormat: {
          format: intl.formatMessage({
            id: '[h]:mm',
          }),
        },
        alignment: {
          horizontal: 'Center',
        },
      },
      {
        id: 'centerCell',
        alignment: {
          horizontal: 'Center',
        },
      },
      {
        id: 'justifyRight',
        alignment: {
          horizontal: 'Right',
        },
      },
      {
        id: 'formulaTimeCell',
        numberFormat: {
          format: '[h]:mm',
        },
        alignment: {
          horizontal: 'Right',
        },
      },
      {
        id: 'formulaTimeCell',
        dataType: 'Formula',
      },
      {
        id: 'formulaCell',
        dataType: 'Formula',
      },
    ],
    processUnpinnedColumns: () => [],
  }
}

const validateTime = (value: string): boolean => {
  if (!value) return true
  if (
    /^([01０１]?[0-9０-９]|[2２][0-3０-３])([:：])([0-5０-５][0-9０-９])$/.test(
      value
    )
  ) {
    return true
  }
  return /^([01０１]?[0-9０-９]|[2２][0-3０-３])([0-5０-５][0-9０-９])$/.test(
    value
  )
}

const isRequiredOtherSide = (base: string, target: string): boolean => {
  return !!base && !target
}

const COLUMN_FIELD_PREFIX_PROJECT: string = 'project_'

const getCheckboxConditions = (
  params: ICellRendererParams,
  projectUuid: string
) => {
  const data = params.data
  if (!data) return undefined
  const currentStatus = pickRequestStatus(params, projectUuid)
  if (data.type === 'WORK_HOUR') {
    return {
      isInvalidRow: isWorkingDay(data.workingHoursType as WorkingHoursType),
      isApproveDisabled:
        currentStatus !== RequestStatus.REQUESTED &&
        currentStatus !== undefined,
      isSentBackDisabled:
        currentStatus === RequestStatus.SENT_BACK ||
        currentStatus === undefined,
      isSentBack: currentStatus === RequestStatus.SENT_BACK,
      isApprove: currentStatus === RequestStatus.APPROVED,
    }
  }
}

const createCheckboxValueSetter = (
  isApprove: boolean,
  params: ICellRendererParams<WorkingHoursRowIncludingCalculated>,
  data: any,
  projectUuid: string
) => {
  const d = params.data
  if (!d) return undefined
  if (d.type === 'SUM' || d.type === 'RATIO') {
    return undefined
  }
  return (cellParams: any, oldValue: any, newValue: any) => {
    const checkboxConditions = getCheckboxConditions(params, projectUuid)
    const { isApproveDisabled, isSentBackDisabled } = checkboxConditions || {
      isApproveDisabled: false,
      isSentBackDisabled: false,
    }

    if (newValue) {
      const otherFieldSuffix = isApprove ? '_sentBack' : '_approve'
      const otherCheckProperty = isApprove ? 'sentBackCheck' : 'approveCheck'
      const shouldClearOther = isApprove
        ? !isSentBackDisabled
        : !isApproveDisabled

      if (data[otherCheckProperty] && shouldClearOther) {
        const otherField =
          COLUMN_FIELD_PREFIX_PROJECT + projectUuid + otherFieldSuffix
        const node =
          cellParams.api?.getRowNode?.(cellParams.node.id) || params.node
        node.setDataValue(otherField, false)
        data[otherCheckProperty] = false
      }
    }

    const node = cellParams.api?.getRowNode?.(cellParams.node.id) || params.node
    const field = cellParams.colDef?.field || params.colDef?.field
    node.setDataValue(field, newValue)

    if (isApprove) {
      data.approveCheck = newValue === true
    } else {
      data.sentBackCheck = newValue === true
    }
    return true
  }
}

export type RefreshDynamicColumnDefProps = {
  api: GridApi | null | undefined
  projects: ProjectDetail[]
  userUuid: string | undefined
  openTaskActualWorkList?: (props: openTaskActualWorkListProps) => void
  data?: WorkingHoursRowIncludingCalculated[]
}

export const workingProject = (
  data: WorkingHoursRowIncludingCalculated[] | ActualWorkingHoursRow[],
  projects: ProjectDetail[],
  currentProject: string | undefined
) => {
  const displayProjectUuids = new Set<string>()
  if (currentProject) {
    displayProjectUuids.add(currentProject)
  }
  data?.forEach(node => {
    if (!node.projectHours) return
    node.projectHours?.forEach(projectHour => {
      if (projectHour.actualHour && projectHour.actualHour > 0) {
        displayProjectUuids.add(projectHour.projectUuid)
      }
    })
  })

  return projects.filter(project => displayProjectUuids.has(project.uuid))
}

export const refreshDynamicColumnDef = ({
  api,
  projects,
  userUuid,
  openTaskActualWorkList,
  data,
}: RefreshDynamicColumnDefProps) => {
  if (!api || _.isEmpty(projects)) return
  const columnDefs = api.getColumnDefs()
  const newColumnDefs = columnDefs?.filter(
    def =>
      !('field' in def) || !def.field?.startsWith(COLUMN_FIELD_PREFIX_PROJECT)
  )
  const currentProject = store.getState().project?.current?.uuid
  if (!newColumnDefs) return

  const displayableProjects = workingProject(
    data || [],
    projects,
    currentProject
  )

  displayableProjects.forEach(project => {
    if (featureFlagService.isEnabled('task-actual-work-application')) {
      newColumnDefs?.push({
        headerName: project.displayName,
        field: COLUMN_FIELD_PREFIX_PROJECT + project.uuid,
        children: [
          {
            field:
              COLUMN_FIELD_PREFIX_PROJECT + project.uuid + '_requestStatus',
            headerName: intl.formatMessage({
              id: 'actualWorkingHours.requestStatus',
            }),
            hide: false,
            pinned: false,
            editable: false,
            width: 120,
            refData: { uuid: project.uuid, code: project.code },
            cellRenderer: RequestStatusCellRenderer,
            valueGetter: (
              params: ValueGetterParams<WorkingHoursRowIncludingCalculated>
            ) => {
              const uuid = params.colDef.refData?.uuid
              const data = params.data
              if (!data) return undefined
              if (data.type === 'WORK_HOUR') {
                const targetProjectHour = data.projectRequestStatus?.find(
                  p => p.projectUuid === uuid
                )
                const requestStatus =
                  targetProjectHour?.requestStatus || undefined
                if (requestStatus === undefined) return ''
                return intl.formatMessage({
                  id:
                    'actualWorkingHours.requestStatus.' +
                    requestStatus.valueOf().toString().toLowerCase(),
                })
              } else if (data.type === 'RATIO') {
                const targetProjectRequest = data.projectRequestStatus?.find(
                  p => p.projectUuid === uuid
                )
                return targetProjectRequest?.approvedStatusConunt ===
                  undefined &&
                  targetProjectRequest?.requestStatusConunt === undefined
                  ? ''
                  : targetProjectRequest?.approvedStatusConunt +
                      '/' +
                      targetProjectRequest?.requestStatusConunt
              }
            },
            valueFormatter: (params: ValueFormatterParams) => params.value,
          },
          {
            field: COLUMN_FIELD_PREFIX_PROJECT + project.uuid + '_actualHour',
            headerName: intl.formatMessage({
              id: 'actualWorkingHours.actualHour',
            }),
            headerTooltip: intl.formatMessage({
              id: 'taskActualWorkApprove.actualHour.description',
            }),
            headerComponentParams: {
              template: customHeaderTemplate({
                tooltip: intl.formatMessage({
                  id: 'taskActualWorkApprove.actualHour.description',
                }),
              }),
            },
            hide: true,
            pinned: false,
            editable: false,
            refData: { uuid: project.uuid, code: project.code },
            valueGetter: (
              params: ValueGetterParams<WorkingHoursRowIncludingCalculated>
            ) => {
              const uuid = params.colDef.refData?.uuid
              const data = params.data
              if (!data) return undefined
              const targetProjectHour = data.projectHours?.find(
                p => p.projectUuid === uuid
              )

              if (data.type === 'RATIO') {
                const totalActualHour = data.totalActualHour || 0
                if (totalActualHour === 0) return []
                return (
                  calculateRatio(
                    targetProjectHour?.actualHour ?? 0,
                    totalActualHour
                  ) + '%'
                )
              }
              const hour = targetProjectHour?.actualHour || 0
              if (hour === 0) return ''
              return formatTime(hour)
            },
            valueFormatter: (params: ValueFormatterParams) => params.value,
            cellStyle: (params: CellClassParams) => {
              const isCurrentProject = project.uuid === currentProject
              const clickable = projectColumnClickable(
                params.data,
                params.colDef.refData?.uuid
              )

              if (!isCurrentProject) {
                return {
                  justifyContent: 'center',
                  cursor: 'default',
                  color: '#000000',
                }
              }

              return getActualWorkColumnCellStyle(() => clickable)
            },
            onCellClicked: (event: CellClickedEvent) => {
              if (
                !projectColumnClickable(
                  event.data,
                  event.colDef.refData?.uuid
                ) ||
                project.uuid !== currentProject
              ) {
                return
              }
              onActualWorkCellClicked(event, userUuid, openTaskActualWorkList)
            },
            cellClass: 'actualTime',
            width: 125,
          },
          ...(project.uuid === currentProject
            ? [
                {
                  field:
                    COLUMN_FIELD_PREFIX_PROJECT + project.uuid + '_approve',
                  headerName: intl.formatMessage({
                    id: 'taskActualWork.approve',
                  }),
                  headerTooltip: intl.formatMessage({
                    id: 'taskActualWorkApprove.approve.description',
                  }),
                  headerComponentParams: {
                    template: customHeaderTemplate({
                      tooltip: intl.formatMessage({
                        id: 'taskActualWorkApprove.approve.description',
                      }),
                    }),
                  },
                  hide: false,
                  pinned: false,
                  refData: { uuid: project.uuid, code: project.code },
                  editable: (
                    params: EditableCallbackParams<WorkingHoursRowIncludingCalculated>
                  ) => {
                    const data = params.data
                    if (!data) return false
                    if (
                      data.type === 'SUM' ||
                      data.type === 'RATIO' ||
                      !pickRequestStatus(params, project.uuid)
                    ) {
                      return false
                    }
                    return (
                      data.type === 'WORK_HOUR' &&
                      isWorkingDay(data.workingHoursType as WorkingHoursType)
                    )
                  },
                  cellClass: params => {
                    const status = pickRequestStatus(params, project.uuid)
                    if (
                      status !== RequestStatus.REQUESTED &&
                      status !== undefined
                    ) {
                      return 'not-edit-check-box'
                    }
                  },
                  cellRendererSelector: (params: ICellRendererParams) => {
                    const data = params.data
                    data.approveCheck = params.value === true

                    const checkboxConditions = getCheckboxConditions(
                      params,
                      project.uuid
                    )
                    const { isApproveDisabled, isApprove } =
                      checkboxConditions || {
                        isApproveDisabled: false,
                        isApprove: false,
                      }

                    if (!pickRequestStatus(params, project.uuid)) {
                      return { component: null }
                    } else if (isApproveDisabled) {
                      params.context.checkboxDisabled = true
                      return {
                        component: CheckBoxCellRenderer,
                        params: {
                          enableDisabledByContext: true,
                          value: isApprove,
                          valueSetter: createCheckboxValueSetter(
                            true,
                            params,
                            data,
                            project.uuid
                          ),
                        },
                      }
                    }
                    return {
                      component: CheckBoxCellRenderer,
                      params: {
                        valueSetter: createCheckboxValueSetter(
                          true,
                          params,
                          data,
                          project.uuid
                        ),
                      },
                    }
                  },
                  valueFormatter: (params: ValueFormatterParams) => {
                    const data = params.data
                    if (
                      !!data?.schedule?.legal ||
                      data?.schedule?.workHour === 0
                    ) {
                      return ''
                    }
                    return params.value === undefined
                      ? undefined
                      : params.value.toString()
                  },
                  keyCreator: (params): string => {
                    return params.value ? 'checked' : ''
                  },
                },
                {
                  field:
                    COLUMN_FIELD_PREFIX_PROJECT + project.uuid + '_sentBack',
                  headerName: intl.formatMessage({
                    id: 'taskActualWork.sentBack',
                  }),
                  headerTooltip: intl.formatMessage({
                    id: 'taskActualWorkApprove.sendback.description',
                  }),
                  headerComponentParams: {
                    template: customHeaderTemplate({
                      tooltip: intl.formatMessage({
                        id: 'taskActualWorkApprove.sendback.description',
                      }),
                    }),
                  },
                  hide: false,
                  pinned: false,
                  refData: { uuid: project.uuid, code: project.code },
                  cellEditor: CheckBoxCellEditor,
                  editable: (
                    params: EditableCallbackParams<WorkingHoursRowIncludingCalculated>
                  ) => {
                    const data = params.data
                    if (!data) return false
                    if (
                      data.type === 'SUM' ||
                      data.type === 'RATIO' ||
                      !pickRequestStatus(params, project.uuid)
                    ) {
                      return false
                    }
                    return (
                      data.type === 'WORK_HOUR' &&
                      isWorkingDay(data.workingHoursType as WorkingHoursType)
                    )
                  },
                  cellClass: params => {
                    const status = pickRequestStatus(params, project.uuid)
                    if (
                      status === RequestStatus.SENT_BACK &&
                      status !== undefined
                    ) {
                      return 'not-edit-check-box'
                    }
                  },
                  cellRendererSelector: (params: ICellRendererParams) => {
                    const data = params.data
                    data.sentBackCheck = params.value === true

                    const checkboxConditions = getCheckboxConditions(
                      params,
                      project.uuid
                    )
                    const { isSentBackDisabled, isSentBack } =
                      checkboxConditions || {
                        isSentBackDisabled: false,
                        isSentBack: false,
                      }

                    if (!pickRequestStatus(params, project.uuid)) {
                      return { component: null }
                    } else if (isSentBackDisabled) {
                      params.context.checkboxDisabled = true
                      return {
                        component: CheckBoxCellRenderer,
                        params: {
                          enableDisabledByContext: true,
                          value: isSentBack,
                        },
                      }
                    }
                    return {
                      component: CheckBoxCellRenderer,
                      params: {
                        valueSetter: createCheckboxValueSetter(
                          false,
                          params,
                          data,
                          project.uuid
                        ),
                      },
                    }
                  },
                  valueFormatter: (params: ValueFormatterParams) => {
                    const data = params.data
                    if (
                      !!data?.schedule?.legal ||
                      data?.schedule?.workHour === 0
                    ) {
                      return ''
                    }
                    return params.value === undefined
                      ? undefined
                      : params.value.toString()
                  },
                  keyCreator: (params): string => {
                    return params.value ? 'checked' : ''
                  },
                  width: 125,
                },
              ]
            : []),
        ],
      })
    }
  })
  api.setGridOption('columnDefs', newColumnDefs)
}

export const updateDisplayProjectColumn = (
  gridApi: GridApi | null | undefined,
  data: ActualWorkingHoursRow[]
) => {
  if (!gridApi || _.isEmpty(data)) return

  const displayProjectUuids = new Set<string>(
    data
      .filter(d => !!d.projectHours)
      .flatMap(d => d.projectHours)
      .map(p => p?.projectUuid)
      .filter(p => !!p) as string[]
  )

  const columnState = gridApi.getColumnState()
  columnState.forEach(state => {
    if (!state.colId.startsWith(COLUMN_FIELD_PREFIX_PROJECT)) return

    const uuidWithSuffix = state.colId.slice(COLUMN_FIELD_PREFIX_PROJECT.length)
    const uuid = uuidWithSuffix.split('_')[0]
    state.hide = !displayProjectUuids.has(uuid)
  })
  gridApi.applyColumnState({
    state: columnState,
    applyOrder: true,
  })

  const columnDefs: (ColDef | ColGroupDef)[] = gridApi.getColumnDefs() || []
  const visibleColDefs = columnDefs.filter(def => {
    if ('children' in def && Array.isArray(def.children)) return true
    if (!('colId' in def) || !def?.colId) return false
    if (!def.colId.startsWith(COLUMN_FIELD_PREFIX_PROJECT)) return true
    const uuid = def.colId.slice(COLUMN_FIELD_PREFIX_PROJECT.length)
    return displayProjectUuids.has(uuid)
  })
  gridApi.getToolPanelInstance('columns')?.setColumnLayout(visibleColDefs)
}

const projectColumnClickable = (
  row: WorkingHoursRowIncludingCalculated,
  projectUuid: string | undefined
): boolean => {
  return (
    row.type !== 'SUM' &&
    row.type !== 'RATIO' &&
    !!row.projectHours &&
    row.projectHours.some(p => p.projectUuid === projectUuid)
  )
}

const pickRequestStatus = (params, projectUuid) => {
  const data = params.data
  if (!data) return
  const requestStatus = data.projectRequestStatus?.find(
    status => status.projectUuid === projectUuid
  )
  if (!requestStatus) return
  return requestStatus?.requestStatus
}

const totalActualHourColumnClickable = (
  row: WorkingHoursRowIncludingCalculated
) => {
  if (row.type === 'SUM' || row.type === 'RATIO') return false
  return !!row.totalActualHour && row.totalActualHour >= 0.01
}

const getActualWorkColumnCellStyle = (clickable: () => boolean) => {
  const style: CellStyle = {
    justifyContent: 'center',
  }
  if (!clickable()) return style

  style.cursor = 'pointer'
  style.color = Color.MAIN
  return style
}

const onActualWorkCellClicked = (
  event: CellClickedEvent,
  userUuid: string | undefined,
  openTaskActualWorkList?: (props: openTaskActualWorkListProps) => void,
  crossProject?: boolean
) => {
  if (!openTaskActualWorkList || !(event.event instanceof PointerEvent)) {
    return
  }
  const row: ActualWorkingHoursRow = event.data
  const projectUuid = event.context.projectUuid ?? event.colDef.refData?.uuid
  const projectCode = event.context.projectCode ?? event.colDef.refData?.code
  openTaskActualWorkList({
    userUuid,
    projectUuid: crossProject ? undefined : projectUuid,
    projectCode: crossProject ? undefined : projectCode,
    targetDate: row.date,
    event: event.event,
  })
}

const parseTimeFormat = (str: string) => {
  str = str.replace('：', ':')
  str = str.replace(/[０-９]/g, s => {
    return String.fromCharCode(s.charCodeAt(0) - 65248)
  })
  if (str.includes(':')) {
    return str
  }
  if (str.length === 4) {
    return `${str.substring(0, 2)}:${str.substring(2)}`
  }
  if (str.length === 3) {
    return `${str.substring(0, 1)}:${str.substring(1)}`
  }
  return undefined
}

export const bulkApproveCheck = (
  data: any,
  gridApi?: GridApi | null | undefined
) => {
  const projectUuid = store.getState().project.selected
  const today = new DateVO().formatForApi()
  const targets = isValidApprove(data, projectUuid!).filter(v => {
    return today && v.date && today >= v.date
  })
  targets.forEach(row => {
    row.approveCheck = true
    row.sentBackCheck = false
  })
  if (gridApi && projectUuid) {
    const approveField = COLUMN_FIELD_PREFIX_PROJECT + projectUuid + '_approve'
    const sentBackField =
      COLUMN_FIELD_PREFIX_PROJECT + projectUuid + '_sentBack'

    targets.forEach(row => {
      const rowNode = gridApi.getRowNode(row.uuid)
      if (rowNode) {
        rowNode.setDataValue(approveField, true)
        rowNode.setDataValue(sentBackField, false)
      }
    })

    gridApi.refreshCells({
      columns: [approveField, sentBackField],
      force: true,
    })
  }

  return targets
}

export const bulkSentBackCheck = (
  data: any,
  gridApi?: GridApi | null | undefined
) => {
  const projectUuid = store.getState().project.selected
  const today = new DateVO().formatForApi()
  const targets = isValidSentBack(data, projectUuid!).filter(v => {
    return today && v.date && today >= v.date
  })
  targets.forEach(row => {
    row.sentBackCheck = true
    row.approveCheck = false
  })
  if (gridApi && projectUuid) {
    const approveField = COLUMN_FIELD_PREFIX_PROJECT + projectUuid + '_approve'
    const sentBackField =
      COLUMN_FIELD_PREFIX_PROJECT + projectUuid + '_sentBack'

    targets.forEach(row => {
      const rowNode = gridApi.getRowNode(row.uuid)
      if (rowNode) {
        rowNode.setDataValue(sentBackField, true)
        rowNode.setDataValue(approveField, false)
      }
    })
    gridApi.refreshCells({
      columns: [approveField, sentBackField],
      force: true,
    })
  }

  return targets
}
